import React, { useState, useRef } from 'react';
import { FileText, BookOpen, Image as ImageIcon, Star, HelpCircle, Megaphone, ArrowLeft, Edit2, Eye, Save, Plus, Trash2, ChevronUp, ChevronDown, EyeOff, Copy, Upload, Search, Filter, CheckCircle2, XCircle, ExternalLink, Calendar, Tag, FileImage, FileVideo, File, X, Percent, Clock, Pin } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { ImageUpload } from '../ui/ImageUpload';
import { useToast } from '../ui/Toast';
import { useBlog, type CmsBlogPost } from '../../contexts/BlogContext';
type CmsView = 'dashboard' | 'pages' | 'page_editor' | 'blog' | 'blog_editor' | 'media' | 'reviews' | 'faq' | 'promotions';
interface PageSection {
  id: string;
  name: string;
  title: string;
  description: string;
  buttonText: string;
  imageUrl: string;
  visible: boolean;
}
interface SitePage {
  id: string;
  title: string;
  slug: string;
  status: string;
  lastModified: string;
  group: string;
}
interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  status: string;
  date: string;
  views: number;
  content: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  imageUrl: string;
  tags: string[];
}
interface MediaItem {
  id: number;
  name: string;
  type: 'image' | 'video' | 'document';
  size: string;
  date: string;
  url: string;
  color: string;
}
interface ReviewItem {
  id: number;
  author: string;
  rating: number;
  text: string;
  date: string;
  status: 'published' | 'pending' | 'rejected';
  service: string;
}
interface FaqItem {
  id: number;
  category: string;
  question: string;
  answer: string;
}
interface Promotion {
  id: number;
  name: string;
  discount: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'scheduled' | 'expired';
  usageCount: number;
  code: string;
}
interface CmsManagerProps {
  onNavigate: (page: string) => void;
}
export function CmsManager({
  onNavigate
}: CmsManagerProps) {
  const {
    success
  } = useToast();
  const {
    cmsPosts,
    setCmsPosts
  } = useBlog();
  const [currentView, setCurrentView] = useState<CmsView>('dashboard');
  // --- PAGES STATE ---
  const [selectedPage, setSelectedPage] = useState<SitePage | null>(null);
  const [pagesFilter, setPagesFilter] = useState<string>('all');
  const [pages] = useState<SitePage[]>([
  // Основные
  {
    id: 'home',
    title: 'Главная',
    slug: '/',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Основные'
  }, {
    id: 'about',
    title: 'О нас',
    slug: '/about',
    status: 'published',
    lastModified: '10 Мар 2026',
    group: 'Основные'
  }, {
    id: 'contact',
    title: 'Контакты',
    slug: '/contact',
    status: 'published',
    lastModified: '12 Мар 2026',
    group: 'Основные'
  }, {
    id: 'pricing',
    title: 'Цены',
    slug: '/pricing',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Основные'
  }, {
    id: 'faq',
    title: 'FAQ',
    slug: '/faq',
    status: 'published',
    lastModified: '08 Мар 2026',
    group: 'Основные'
  }, {
    id: 'blog',
    title: 'Блог',
    slug: '/blog',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Основные'
  }, {
    id: 'free-chart',
    title: 'Бесплатная карта',
    slug: '/free-chart',
    status: 'published',
    lastModified: '05 Мар 2026',
    group: 'Основные'
  }, {
    id: 'privacy',
    title: 'Политика конфиденциальности',
    slug: '/privacy',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Основные'
  }, {
    id: 'terms',
    title: 'Условия использования',
    slug: '/terms',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Основные'
  },
  // Услуги
  {
    id: 'service-natal',
    title: 'Натальная карта',
    slug: '/service-natal',
    status: 'published',
    lastModified: '13 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-solar',
    title: 'Соляр',
    slug: '/service-solar',
    status: 'published',
    lastModified: '13 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-compatibility',
    title: 'Совместимость',
    slug: '/service-compatibility',
    status: 'published',
    lastModified: '12 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-combo',
    title: 'Комбо-анализ',
    slug: '/service-combo',
    status: 'published',
    lastModified: '12 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-forecast',
    title: 'Прогноз транзитов',
    slug: '/service-forecast',
    status: 'published',
    lastModified: '11 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-consultation',
    title: 'Консультация',
    slug: '/service-consultation',
    status: 'published',
    lastModified: '11 Мар 2026',
    group: 'Услуги'
  },
  // Ежедневник
  {
    id: 'astro-daily',
    title: 'Астро-ежедневник',
    slug: '/astro-daily',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Ежедневник'
  }, {
    id: 'daily-horoscope',
    title: 'Ежедневный гороскоп',
    slug: '/daily-horoscope',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Ежедневник'
  }, {
    id: 'moon-calendar',
    title: 'Лунный календарь',
    slug: '/moon-calendar',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Ежедневник'
  },
  // Каталог
  {
    id: 'astrologers',
    title: 'Каталог астрологов',
    slug: '/astrologers',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Каталог'
  }, {
    id: 'interpretations',
    title: 'Интерпретации',
    slug: '/interpretations',
    status: 'published',
    lastModified: '10 Мар 2026',
    group: 'Каталог'
  }, {
    id: 'calendar',
    title: 'Астро-календарь',
    slug: '/calendar',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Каталог'
  }, {
    id: 'marketplace',
    title: 'Маркетплейс услуг',
    slug: '/marketplace',
    status: 'published',
    lastModified: '09 Мар 2026',
    group: 'Каталог'
  },
  // Маркетинг
  {
    id: 'for-astrologers',
    title: 'Для астрологов',
    slug: '/for-astrologers',
    status: 'published',
    lastModified: '08 Мар 2026',
    group: 'Маркетинг'
  }, {
    id: 'advertising',
    title: 'Реклама на сайте',
    slug: '/advertising',
    status: 'published',
    lastModified: '05 Мар 2026',
    group: 'Маркетинг'
  },
  // Пользователи
  {
    id: 'login',
    title: 'Авторизация',
    slug: '/login',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Пользователи'
  }, {
    id: 'checkout',
    title: 'Оформление заказа',
    slug: '/checkout',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Пользователи'
  }, {
    id: 'dashboard-user',
    title: 'Личный кабинет',
    slug: '/dashboard-user',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Пользователи'
  }, {
    id: 'dashboard-astrologer',
    title: 'Кабинет астролога',
    slug: '/dashboard-astrologer',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Пользователи'
  }]);
  const [pageSections, setPageSections] = useState<Record<string, PageSection[]>>({
    home: [{
      id: 'hero',
      name: 'Hero баннер',
      title: 'Ваш путеводитель по звёздам',
      description: 'Профессиональные астрологические услуги: натальные карты, прогнозы, совместимость и консультации с опытными астрологами.',
      buttonText: 'Рассчитать карту',
      imageUrl: '',
      visible: true
    }, {
      id: 'services',
      name: 'Быстрые услуги',
      title: 'Наши услуги',
      description: 'Натальная карта, Соляр, Комбо: Натал + Соляр, Совместимость, Прогноз, Консультация — выберите подходящий формат.',
      buttonText: 'Все услуги',
      imageUrl: '',
      visible: true
    }, {
      id: 'advantages',
      name: 'Преимущества',
      title: 'Почему выбирают Astriqo',
      description: 'Точные расчёты по швейцарским эфемеридам, профессиональные астрологи с верификацией, мгновенные авто-отчёты, удобный личный кабинет.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'packages',
      name: 'Пакеты отчётов',
      title: 'Выгодные пакеты',
      description: 'Комбинируйте услуги и экономьте до 30%. Пакет «Базовый», «Расширенный», «Полный».',
      buttonText: 'Смотреть пакеты',
      imageUrl: '',
      visible: true
    }, {
      id: 'reviews',
      name: 'Отзывы клиентов',
      title: 'Что говорят наши клиенты',
      description: 'Реальные отзывы пользователей о натальных картах, прогнозах и консультациях.',
      buttonText: 'Все отзывы',
      imageUrl: '',
      visible: true
    }, {
      id: 'cta',
      name: 'CTA блок',
      title: 'Готовы узнать свою судьбу?',
      description: 'Закажите персональный астрологический разбор прямо сейчас. Первый отчёт со скидкой 20%.',
      buttonText: 'Начать сейчас',
      imageUrl: '',
      visible: true
    }],
    about: [{
      id: 'hero',
      name: 'Hero',
      title: 'Делаем профессиональную астрологию доступной',
      description: 'Astriqo — это платформа нового поколения, объединяющая точные алгоритмы расчета, глубокие знания профессиональных астрологов и современные технологии.',
      buttonText: 'Рассчитать карту',
      imageUrl: '',
      visible: true
    }, {
      id: 'mission',
      name: 'Миссия',
      title: 'Наша миссия',
      description: 'Сделать качественную астрологию доступной каждому. Мы верим, что понимание своей натальной карты помогает принимать осознанные решения.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'values',
      name: 'Ценности',
      title: 'Наши ценности',
      description: 'Точность расчётов, научный подход, верификация астрологов, прозрачность, защита данных, постоянное развитие.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'team',
      name: 'Команда',
      title: 'Наша команда',
      description: 'Более 50 верифицированных астрологов, команда разработчиков и аналитиков. Средний опыт астрологов — 12 лет.',
      buttonText: 'Наши астрологи',
      imageUrl: '',
      visible: true
    }, {
      id: 'stats',
      name: 'Статистика',
      title: 'Astriqo в цифрах',
      description: '50 000+ пользователей, 120 000+ отчётов, 50+ астрологов, 4.8 средний рейтинг.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    contact: [{
      id: 'hero',
      name: 'Hero',
      title: 'Свяжитесь с нами',
      description: 'Мы всегда рады помочь. Выберите удобный способ связи или заполните форму обратной связи.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'form',
      name: 'Форма обратной связи',
      title: 'Написать нам',
      description: 'Заполните форму, и мы ответим в течение 24 часов. Поля: Имя, Email, Тема, Сообщение.',
      buttonText: 'Отправить',
      imageUrl: '',
      visible: true
    }, {
      id: 'contacts',
      name: 'Контактная информация',
      title: 'Контакты',
      description: 'Email: support@astriqo.com | Telegram: @astriqo_support | Время работы: Пн-Пт 10:00-19:00 МСК',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq_link',
      name: 'Ссылка на FAQ',
      title: 'Часто задаваемые вопросы',
      description: 'Возможно, ответ на ваш вопрос уже есть в нашем разделе FAQ.',
      buttonText: 'Перейти в FAQ',
      imageUrl: '',
      visible: true
    }],
    pricing: [{
      id: 'hero',
      name: 'Hero',
      title: 'Тарифы и цены',
      description: 'Прозрачные цены на все астрологические услуги. Выберите подходящий формат: авто-отчёт или консультация с астрологом.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'auto_reports',
      name: 'Авто-отчёты',
      title: 'Автоматические отчёты',
      description: 'Натальная карта от 3 900 ₽, Соляр от 4 400 ₽, Совместимость от 4 900 ₽, Комбо от 6 900 ₽, Прогноз от 3 900 ₽.',
      buttonText: 'Выбрать отчёт',
      imageUrl: '',
      visible: true
    }, {
      id: 'consultations',
      name: 'Консультации',
      title: 'Консультации с астрологом',
      description: 'Видеоконсультация 60 мин от 2 990 ₽, Расширенная 90 мин от 4 490 ₽, VIP 120 мин от 6 990 ₽.',
      buttonText: 'Записаться',
      imageUrl: '',
      visible: true
    }, {
      id: 'packages',
      name: 'Пакеты',
      title: 'Выгодные пакеты',
      description: 'Базовый (Натал + Соляр) — 6 900 ₽, Расширенный (+Совместимость) — 10 900 ₽, Полный (все отчёты) — 15 900 ₽.',
      buttonText: 'Выбрать пакет',
      imageUrl: '',
      visible: true
    }, {
      id: 'comparison',
      name: 'Сравнение',
      title: 'Сравнение тарифов',
      description: 'Таблица сравнения: что входит в каждый тариф, формат доставки, сроки, гарантии.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ по ценам',
      title: 'Вопросы о ценах',
      description: 'Есть ли рассрочка? Можно ли вернуть деньги? Какие способы оплаты? Есть ли скидки?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    faq: [{
      id: 'hero',
      name: 'Hero',
      title: 'Часто задаваемые вопросы',
      description: 'Ответы на популярные вопросы о наших услугах, оплате, консультациях и технических моментах.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Разделы FAQ',
      description: 'Общие вопросы, Отчёты и услуги, Оплата и возврат, Консультации, Астрологи, Техподдержка.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'contact_cta',
      name: 'CTA поддержки',
      title: 'Не нашли ответ?',
      description: 'Напишите нам, и мы ответим в течение 24 часов.',
      buttonText: 'Написать в поддержку',
      imageUrl: '',
      visible: true
    }],
    blog: [{
      id: 'hero',
      name: 'Hero',
      title: 'Блог Astriqo',
      description: 'Статьи об астрологии, лунные календари, прогнозы, обучающие материалы и советы от профессиональных астрологов.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Разделы блога',
      description: 'Астрология, Обучение, Лунный календарь, Совместимость, Советы, Практики, Новости.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'featured',
      name: 'Избранные статьи',
      title: 'Популярное',
      description: 'Блок с избранными и самыми читаемыми статьями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'free-chart': [{
      id: 'hero',
      name: 'Hero',
      title: 'Бесплатная натальная карта',
      description: 'Рассчитайте свою натальную карту бесплатно. Введите дату, время и место рождения для мгновенного результата.',
      buttonText: 'Рассчитать бесплатно',
      imageUrl: '',
      visible: true
    }, {
      id: 'form',
      name: 'Форма расчёта',
      title: 'Данные для расчёта',
      description: 'Поля: Дата рождения, Время рождения, Город рождения. Точное время даёт более точный результат.',
      buttonText: 'Рассчитать',
      imageUrl: '',
      visible: true
    }, {
      id: 'upsell',
      name: 'Апсейл',
      title: 'Хотите полный разбор?',
      description: 'Бесплатная карта — это только начало. Закажите полный отчёт с детальными интерпретациями.',
      buttonText: 'Заказать полный отчёт',
      imageUrl: '',
      visible: true
    }],
    privacy: [{
      id: 'title',
      name: 'Заголовок',
      title: 'Политика конфиденциальности',
      description: 'Настоящая политика конфиденциальности описывает порядок обработки персональных данных пользователей сервиса Astriqo.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'content',
      name: 'Содержание',
      title: 'Основные положения',
      description: 'Сбор данных, использование, хранение, передача третьим лицам, права пользователей, cookies, изменения политики.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    terms: [{
      id: 'title',
      name: 'Заголовок',
      title: 'Условия использования',
      description: 'Настоящие условия регулируют использование сервиса Astriqo и предоставляемых услуг.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'content',
      name: 'Содержание',
      title: 'Основные положения',
      description: 'Регистрация, оплата, возврат, интеллектуальная собственность, ограничение ответственности, разрешение споров.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-natal': [{
      id: 'hero',
      name: 'Hero',
      title: 'Натальная карта',
      description: 'Глубокий анализ вашей личности, талантов, кармических задач и жизненного пути на основе точного расчёта натальной карты.',
      buttonText: 'Заказать разбор',
      imageUrl: '',
      visible: true
    }, {
      id: 'what_is',
      name: 'Что это',
      title: 'Что такое натальная карта?',
      description: 'Натальная карта — это карта неба в момент вашего рождения. Она показывает расположение планет по знакам и домам.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Анализ Солнца, Луны, Асцендента, планет в знаках и домах, аспекты, кармические узлы, таланты, профессия, отношения.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы отчёта',
      description: 'Базовый (авто-отчёт PDF) от 3 900 ₽ — мгновенно. Премиум (ручной разбор астрологом) от 7 900 ₽ — 3-5 дней.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'reviews',
      name: 'Отзывы',
      title: 'Отзывы клиентов',
      description: 'Реальные отзывы пользователей, заказавших натальную карту.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ',
      title: 'Частые вопросы',
      description: 'Нужно ли точное время рождения? Чем отличается от гороскопа? Как долго ждать?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-solar': [{
      id: 'hero',
      name: 'Hero',
      title: 'Соляр — прогноз на год',
      description: 'Персональный прогноз на ваш личный год: ключевые события, возможности и вызовы с точностью до месяца.',
      buttonText: 'Заказать соляр',
      imageUrl: '',
      visible: true
    }, {
      id: 'what_is',
      name: 'Что это',
      title: 'Что такое соляр?',
      description: 'Соляр — это карта, построенная на момент точного возвращения Солнца в позицию при рождении. Показывает темы года.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Общая тема года, помесячный прогноз, сферы активности, благоприятные периоды, рекомендации.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы отчёта',
      description: 'Базовый (авто-отчёт) от 4 400 ₽. Премиум (ручной разбор) от 8 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ',
      title: 'Частые вопросы',
      description: 'Когда лучше заказывать соляр? Чем отличается от натальной карты?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-compatibility': [{
      id: 'hero',
      name: 'Hero',
      title: 'Анализ совместимости',
      description: 'Детальный разбор отношений двух людей: синастрия, кармические связи, зоны роста и потенциал пары.',
      buttonText: 'Заказать анализ',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Синастрия (наложение карт), композит (карта отношений), кармические связи, рекомендации для пары.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы отчёта',
      description: 'Базовый от 4 900 ₽. Премиум от 9 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ',
      title: 'Частые вопросы',
      description: 'Нужны ли данные обоих партнёров? Можно ли проверить совместимость с другом?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-combo': [{
      id: 'hero',
      name: 'Hero',
      title: 'Комбо: Натал + Соляр',
      description: 'Полная картина: кто вы и что вас ждёт в этом году. Два отчёта по цене одного со скидкой.',
      buttonText: 'Заказать комбо',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит',
      description: 'Полный натальный разбор + годовой прогноз (соляр). Экономия до 25% по сравнению с отдельными заказами.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы',
      description: 'Базовый комбо от 6 900 ₽. Премиум комбо от 14 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }],
    'service-forecast': [{
      id: 'hero',
      name: 'Hero',
      title: 'Прогноз транзитов',
      description: 'Анализ текущих и предстоящих транзитов планет относительно вашей натальной карты. Ключевые периоды и рекомендации.',
      buttonText: 'Заказать прогноз',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Транзиты медленных планет, ключевые даты, благоприятные периоды для бизнеса, отношений, здоровья.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы',
      description: 'Базовый (3 месяца) от 3 900 ₽. Расширенный (12 месяцев) от 6 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }],
    'service-consultation': [{
      id: 'hero',
      name: 'Hero',
      title: 'Консультация с астрологом',
      description: 'Живой разговор с профессиональным астрологом по видеосвязи. Ответы на ваши вопросы, персональные рекомендации.',
      buttonText: 'Записаться',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы консультаций',
      description: 'Экспресс 30 мин от 1 990 ₽, Стандарт 60 мин от 2 990 ₽, Расширенная 90 мин от 4 490 ₽, VIP 120 мин от 6 990 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'how_it_works',
      name: 'Как это работает',
      title: 'Как проходит консультация',
      description: '1. Выберите астролога → 2. Выберите время → 3. Оплатите → 4. Получите ссылку на видеозвонок → 5. Консультация.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'astrologers',
      name: 'Астрологи',
      title: 'Наши астрологи',
      description: 'Все астрологи проходят верификацию. Средний опыт — 12 лет. Рейтинг от 4.5 звёзд.',
      buttonText: 'Выбрать астролога',
      imageUrl: '',
      visible: true
    }],
    'astro-daily': [{
      id: 'hero',
      name: 'Hero',
      title: 'Астро-ежедневник',
      description: 'Ежедневные гороскопы, лунный календарь, астрологические советы дня и планетарные часы.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'horoscope',
      name: 'Гороскоп',
      title: 'Ежедневный гороскоп',
      description: 'Персональный гороскоп на сегодня для каждого знака зодиака с рекомендациями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'moon',
      name: 'Луна сегодня',
      title: 'Лунный день',
      description: 'Текущий лунный день, фаза Луны, знак Луны, рекомендации.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'daily-horoscope': [{
      id: 'hero',
      name: 'Hero',
      title: 'Ежедневный гороскоп',
      description: 'Астрологический прогноз на сегодня для всех знаков зодиака.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'signs',
      name: 'Знаки зодиака',
      title: 'Выберите знак',
      description: 'Овен, Телец, Близнецы, Рак, Лев, Дева, Весы, Скорпион, Стрелец, Козерог, Водолей, Рыбы.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'moon-calendar': [{
      id: 'hero',
      name: 'Hero',
      title: 'Лунный календарь',
      description: 'Подробный лунный календарь с фазами Луны, лунными днями и рекомендациями на каждый день.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'calendar',
      name: 'Календарь',
      title: 'Календарь на месяц',
      description: 'Визуальный календарь с фазами Луны, благоприятными и неблагоприятными днями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    astrologers: [{
      id: 'hero',
      name: 'Hero',
      title: 'Каталог астрологов',
      description: 'Найдите своего астролога. Все специалисты прошли верификацию и имеют подтверждённый опыт.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'filters',
      name: 'Фильтры',
      title: 'Фильтры поиска',
      description: 'Специализация, рейтинг, цена, опыт, язык консультации, доступность.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'catalog',
      name: 'Список астрологов',
      title: 'Наши астрологи',
      description: 'Карточки астрологов с фото, рейтингом, специализацией, ценой и кнопкой записи.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    interpretations: [{
      id: 'hero',
      name: 'Hero',
      title: 'Астрологические интерпретации',
      description: 'Энциклопедия астрологических значений: планеты в знаках, дома, аспекты, транзиты.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Разделы',
      description: 'Планеты в знаках, Планеты в домах, Аспекты, Лунные узлы, Ретрограды.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    calendar: [{
      id: 'hero',
      name: 'Hero',
      title: 'Астрологический календарь',
      description: 'Все важные астрологические события: затмения, ретрограды, ингрессии, полнолуния и новолуния.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'events',
      name: 'События',
      title: 'Ближайшие события',
      description: 'Список предстоящих астрологических событий с датами и описаниями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    marketplace: [{
      id: 'hero',
      name: 'Hero',
      title: 'Маркетплейс астрологических услуг',
      description: 'Все услуги в одном месте: отчёты, консультации, курсы, вебинары от проверенных астрологов.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Категории услуг',
      description: 'Натальные карты, Прогнозы, Совместимость, Консультации, Курсы, Вебинары.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'for-astrologers': [{
      id: 'hero',
      name: 'Hero',
      title: 'Для астрологов',
      description: 'Присоединяйтесь к Astriqo — крупнейшей платформе астрологических услуг. Находите клиентов и развивайте практику.',
      buttonText: 'Стать астрологом',
      imageUrl: '',
      visible: true
    }, {
      id: 'benefits',
      name: 'Преимущества',
      title: 'Почему Astriqo',
      description: 'Готовая клиентская база, удобный кабинет, система записей, автоматические выплаты, маркетинговая поддержка.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'how_to_join',
      name: 'Как начать',
      title: 'Как стать астрологом на платформе',
      description: '1. Заполните анкету → 2. Пройдите верификацию → 3. Настройте профиль → 4. Начните принимать клиентов.',
      buttonText: 'Подать заявку',
      imageUrl: '',
      visible: true
    }],
    advertising: [{
      id: 'hero',
      name: 'Hero',
      title: 'Реклама на Astriqo',
      description: 'Размещайте рекламу на платформе с аудиторией 50 000+ пользователей, интересующихся астрологией.',
      buttonText: 'Обсудить размещение',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы рекламы',
      title: 'Рекламные форматы',
      description: 'Баннеры, нативные статьи, спонсорские интеграции, email-рассылки, спецпроекты.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'audience',
      name: 'Аудитория',
      title: 'Наша аудитория',
      description: '80% женщины 25-45 лет, интересы: астрология, саморазвитие, психология, эзотерика.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    login: [{
      id: 'hero',
      name: 'Форма входа',
      title: 'Вход в аккаунт',
      description: 'Войдите в свой аккаунт Astriqo для доступа к отчётам, консультациям и личному кабинету.',
      buttonText: 'Войти',
      imageUrl: '',
      visible: true
    }, {
      id: 'register',
      name: 'Регистрация',
      title: 'Создать аккаунт',
      description: 'Зарегистрируйтесь для доступа ко всем возможностям платформы.',
      buttonText: 'Зарегистрироваться',
      imageUrl: '',
      visible: true
    }],
    checkout: [{
      id: 'form',
      name: 'Форма заказа',
      title: 'Оформление заказа',
      description: 'Корзина, данные для расчёта, выбор способа оплаты, промокод.',
      buttonText: 'Оплатить',
      imageUrl: '',
      visible: true
    }, {
      id: 'payment',
      name: 'Оплата',
      title: 'Способы оплаты',
      description: 'Банковские карты (Visa, MasterCard, МИР), ЮKassa, Apple Pay, Google Pay.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'dashboard-user': [{
      id: 'overview',
      name: 'Обзор',
      title: 'Личный кабинет',
      description: 'Ваши отчёты, заказы, консультации, подписка и настройки профиля.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'reports',
      name: 'Мои отчёты',
      title: 'Мои отчёты',
      description: 'Список всех приобретённых отчётов с возможностью скачивания и просмотра.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'consultations',
      name: 'Консультации',
      title: 'Мои консультации',
      description: 'Предстоящие и прошедшие консультации с астрологами.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'dashboard-astrologer': [{
      id: 'overview',
      name: 'Обзор',
      title: 'Кабинет астролога',
      description: 'Статистика, записи клиентов, заработок, настройки профиля и расписания.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'schedule',
      name: 'Расписание',
      title: 'Моё расписание',
      description: 'Управление доступными слотами для консультаций.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'clients',
      name: 'Клиенты',
      title: 'Мои клиенты',
      description: 'Список клиентов, история консультаций, заметки.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'earnings',
      name: 'Заработок',
      title: 'Финансы',
      description: 'Баланс, история выплат, статистика заработка.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }]
  });
  // --- BLOG STATE (shared via BlogContext with localStorage persistence) ---
  // DEFAULT_CMS_POSTS now live in BlogContext and persist via localStorage
  const blogPosts = cmsPosts;
  const setBlogPosts = setCmsPosts;
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [postForm, setPostForm] = useState({
    title: '',
    excerpt: '',
    category: 'Астрология',
    status: 'draft',
    content: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    imageUrl: '',
    tags: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [blogSearch, setBlogSearch] = useState('');
  const [blogCategoryFilter, setBlogCategoryFilter] = useState('all');
  const [blogStatusFilter, setBlogStatusFilter] = useState('all');
  const BLOG_CAT_STORAGE_KEY = 'astriqo_blog_categories';
  const defaultBlogCategories = ['Астрология', 'Обучение', 'Календарь', 'Прогнозы', 'Советы', 'Практики', 'Новости'];
  const [blogCategories, setBlogCategories] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(BLOG_CAT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return defaultBlogCategories;
  });
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const addBlogCategory = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || blogCategories.includes(trimmed)) return;
    const updated = [...blogCategories, trimmed];
    setBlogCategories(updated);
    try {
      localStorage.setItem(BLOG_CAT_STORAGE_KEY, JSON.stringify(updated));
    } catch {}
    setNewCategoryInput('');
    setShowNewCategoryInput(false);
    success(`Категория «${trimmed}» добавлена`);
  };
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const removeBlogCategory = (cat: string) => {
    const articlesInCategory = blogPosts.filter(p => p.category === cat);
    if (articlesInCategory.length > 0) {
      setCategoryToDelete(cat);
      return;
    }
    performCategoryDeletion(cat);
  };
  const performCategoryDeletion = (cat: string) => {
    // Update all articles in this category to "Без категории"
    setBlogPosts(prev => prev.map(p => p.category === cat ? {
      ...p,
      category: 'Без категории'
    } : p));
    // Remove category from list
    const updated = blogCategories.filter(c => c !== cat);
    setBlogCategories(updated);
    try {
      localStorage.setItem(BLOG_CAT_STORAGE_KEY, JSON.stringify(updated));
    } catch {}
    // Reset filter if we were filtering by this category
    if (blogCategoryFilter === cat) {
      setBlogCategoryFilter('all');
    }
    setCategoryToDelete(null);
    success(`Категория «${cat}» удалена`);
  };
  const filteredBlogPosts = blogPosts.filter(p => {
    if (blogCategoryFilter !== 'all' && p.category !== blogCategoryFilter) return false;
    if (blogStatusFilter !== 'all' && p.status !== blogStatusFilter) return false;
    if (blogSearch.trim()) {
      const q = blogSearch.toLowerCase();
      if (!p.title.toLowerCase().includes(q) && !p.excerpt.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  // --- MEDIA STATE ---
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([{
    id: 1,
    name: 'hero-banner.jpg',
    type: 'image',
    size: '2.4 MB',
    date: '14 Мар 2026',
    url: '/uploads/hero.jpg',
    color: 'bg-blue-100'
  }, {
    id: 2,
    name: 'natal-chart-example.png',
    type: 'image',
    size: '1.8 MB',
    date: '12 Мар 2026',
    url: '/uploads/natal.png',
    color: 'bg-green-100'
  }, {
    id: 3,
    name: 'promo-video.mp4',
    type: 'video',
    size: '45 MB',
    date: '10 Мар 2026',
    url: '/uploads/promo.mp4',
    color: 'bg-rose-100'
  }, {
    id: 4,
    name: 'terms.pdf',
    type: 'document',
    size: '320 KB',
    date: '01 Мар 2026',
    url: '/uploads/terms.pdf',
    color: 'bg-amber-100'
  }, {
    id: 5,
    name: 'astrologer-photo.jpg',
    type: 'image',
    size: '1.1 MB',
    date: '28 Фев 2026',
    url: '/uploads/astrologer.jpg',
    color: 'bg-purple-100'
  }, {
    id: 6,
    name: 'report-sample.pdf',
    type: 'document',
    size: '2.1 MB',
    date: '25 Фев 2026',
    url: '/uploads/report.pdf',
    color: 'bg-amber-100'
  }]);
  const [mediaFilter, setMediaFilter] = useState<string>('all');
  // --- REVIEWS STATE ---
  const [cmsReviews, setCmsReviews] = useState<ReviewItem[]>([{
    id: 1,
    author: 'Анна К.',
    rating: 5,
    text: 'Потрясающий разбор натальной карты! Очень точно и подробно.',
    date: '14 Мар 2026',
    status: 'published',
    service: 'Натальная карта'
  }, {
    id: 2,
    author: 'Дмитрий П.',
    rating: 4,
    text: 'Хороший прогноз, многое совпало. Рекомендую.',
    date: '12 Мар 2026',
    status: 'published',
    service: 'Прогноз транзитов'
  }, {
    id: 3,
    author: 'Мария С.',
    rating: 5,
    text: 'Консультация была очень полезной, астролог ответил на все вопросы.',
    date: '10 Мар 2026',
    status: 'pending',
    service: 'Консультация'
  }, {
    id: 4,
    author: 'Иван Р.',
    rating: 3,
    text: 'Неплохо, но ожидал более детального анализа.',
    date: '08 Мар 2026',
    status: 'pending',
    service: 'Соляр'
  }, {
    id: 5,
    author: 'Елена В.',
    rating: 5,
    text: 'Лучший сервис! Пользуюсь уже полгода.',
    date: '05 Мар 2026',
    status: 'published',
    service: 'Натальная карта'
  }, {
    id: 6,
    author: 'Спам бот',
    rating: 1,
    text: 'Купите наши товары по ссылке...',
    date: '04 Мар 2026',
    status: 'rejected',
    service: 'Натальная карта'
  }]);
  // --- FAQ STATE ---
  const [faqItems, setFaqItems] = useState<FaqItem[]>([{
    id: 1,
    category: 'Общие вопросы',
    question: 'Что такое натальная карта?',
    answer: 'Натальная карта — это карта неба в момент вашего рождения, показывающая расположение планет.'
  }, {
    id: 2,
    category: 'Общие вопросы',
    question: 'Нужно ли точное время рождения?',
    answer: 'Да, точное время рождения важно для определения Асцендента и домов.'
  }, {
    id: 3,
    category: 'Оплата',
    question: 'Какие способы оплаты доступны?',
    answer: 'Мы принимаем банковские карты, ЮKassa, Apple Pay и Google Pay.'
  }, {
    id: 4,
    category: 'Оплата',
    question: 'Можно ли вернуть деньги?',
    answer: 'Да, в течение 14 дней с момента покупки, если отчёт не был скачан.'
  }, {
    id: 5,
    category: 'Услуги',
    question: 'Чем отличается авто-отчёт от ручного?',
    answer: 'Авто-отчёт генерируется мгновенно алгоритмом. Ручной разбор делает астролог лично.'
  }, {
    id: 6,
    category: 'Услуги',
    question: 'Как долго ждать ручной разбор?',
    answer: 'Обычно 3-5 рабочих дней в зависимости от загруженности астролога.'
  }]);
  // --- PROMOTIONS STATE ---
  const [promotions, setPromotions] = useState<Promotion[]>([{
    id: 1,
    name: 'Весенняя скидка',
    discount: '20%',
    startDate: '01 Мар 2026',
    endDate: '31 Мар 2026',
    status: 'active',
    usageCount: 45,
    code: 'SPRING20'
  }, {
    id: 2,
    name: 'Первый заказ',
    discount: '15%',
    startDate: '01 Янв 2026',
    endDate: '31 Дек 2026',
    status: 'active',
    usageCount: 230,
    code: 'FIRST15'
  }, {
    id: 3,
    name: 'Летний марафон',
    discount: '30%',
    startDate: '01 Июн 2026',
    endDate: '30 Июн 2026',
    status: 'scheduled',
    usageCount: 0,
    code: 'SUMMER30'
  }]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({
    name: '',
    code: '',
    discount: '10%',
    startDate: '',
    endDate: ''
  });
  // --- SECTION HANDLERS (per-page) ---
  const currentSections = selectedPage ? pageSections[selectedPage.id] || [] : [];
  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    const secs = [...(pageSections[pageId] || [])];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= secs.length) return;
    [secs[index], secs[swapIdx]] = [secs[swapIdx], secs[index]];
    setPageSections(prev => ({
      ...prev,
      [pageId]: secs
    }));
  };
  const handleToggleVisibility = (index: number) => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: (prev[pageId] || []).map((s, i) => i === index ? {
        ...s,
        visible: !s.visible
      } : s)
    }));
  };
  const handleSectionChange = (index: number, field: string, value: string) => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: (prev[pageId] || []).map((s, i) => i === index ? {
        ...s,
        [field]: value
      } : s)
    }));
  };
  const handleAddSection = () => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: [...(prev[pageId] || []), {
        id: `section_${Date.now()}`,
        name: 'Новый блок',
        title: '',
        description: '',
        buttonText: '',
        imageUrl: '',
        visible: true
      }]
    }));
    success('Блок добавлен');
  };
  const handleDeleteSection = (index: number) => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: (prev[pageId] || []).filter((_, i) => i !== index)
    }));
    success('Блок удалён');
  };
  const pageGroups = [...new Set(pages.map(p => p.group))];
  const filteredPages = pagesFilter === 'all' ? pages : pages.filter(p => p.group === pagesFilter);
  const openPostEditor = (post: BlogPost | null) => {
    if (post) {
      setSelectedPost(post);
      setPostForm({
        title: post.title,
        excerpt: post.excerpt,
        category: post.category,
        status: post.status,
        content: post.content,
        seoTitle: post.seoTitle,
        seoDescription: post.seoDescription,
        seoKeywords: post.seoKeywords,
        imageUrl: post.imageUrl,
        tags: post.tags || []
      });
    } else {
      setSelectedPost(null);
      setPostForm({
        title: '',
        excerpt: '',
        category: 'Астрология',
        status: 'draft',
        content: '',
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
        imageUrl: '',
        tags: []
      });
    }
    setTagInput('');
    setCurrentView('blog_editor');
  };
  const handleSavePost = (overrideStatus?: string) => {
    if (!postForm.title.trim()) return;
    const formData = overrideStatus ? {
      ...postForm,
      status: overrideStatus
    } : postForm;
    if (selectedPost) {
      setBlogPosts(prev => prev.map(p => p.id === selectedPost.id ? {
        ...p,
        ...formData,
        date: 'Сегодня'
      } : p));
      success(overrideStatus === 'published' ? 'Статья опубликована' : 'Статья обновлена');
    } else {
      setBlogPosts(prev => [{
        id: Date.now(),
        ...formData,
        date: 'Сегодня',
        views: 0
      }, ...prev]);
      success(overrideStatus === 'published' ? 'Статья опубликована' : 'Статья создана');
    }
    setCurrentView('blog');
  };
  const handleDeletePost = (id: number) => {
    setBlogPosts(prev => prev.filter(p => p.id !== id));
    success('Статья удалена');
  };
  const handleTogglePostStatus = (id: number) => {
    setBlogPosts(prev => prev.map(p => p.id === id ? {
      ...p,
      status: p.status === 'published' ? 'draft' : 'published',
      date: 'Сегодня'
    } : p));
    const post = blogPosts.find(p => p.id === id);
    success(post?.status === 'published' ? 'Статья снята с публикации' : 'Статья опубликована');
  };
  const handleDuplicatePost = (post: BlogPost) => {
    setBlogPosts(prev => [{
      ...post,
      id: Date.now(),
      title: `${post.title} (копия)`,
      status: 'draft',
      date: 'Сегодня',
      views: 0
    }, ...prev]);
    success('Статья скопирована');
  };
  const handleReviewAction = (id: number, action: 'publish' | 'reject' | 'delete') => {
    if (action === 'delete') {
      setCmsReviews(prev => prev.filter(r => r.id !== id));
      success('Отзыв удалён');
    } else {
      setCmsReviews(prev => prev.map(r => r.id === id ? {
        ...r,
        status: action === 'publish' ? 'published' : 'rejected'
      } : r));
      success(action === 'publish' ? 'Отзыв опубликован' : 'Отзыв отклонён');
    }
  };
  const handleFaqChange = (id: number, field: 'question' | 'answer', value: string) => {
    setFaqItems(prev => prev.map(f => f.id === id ? {
      ...f,
      [field]: value
    } : f));
  };
  const handleAddFaq = (category: string) => {
    setFaqItems(prev => [...prev, {
      id: Date.now(),
      category,
      question: '',
      answer: ''
    }]);
  };
  const handleDeleteFaq = (id: number) => {
    setFaqItems(prev => prev.filter(f => f.id !== id));
    success('Вопрос удалён');
  };
  const handleDeleteMedia = (id: number) => {
    setMediaItems(prev => prev.filter(m => m.id !== id));
    success('Файл удалён');
  };
  const handleCreatePromo = () => {
    if (!promoForm.name.trim() || !promoForm.code.trim()) return;
    setPromotions(prev => [...prev, {
      id: Date.now(),
      name: promoForm.name,
      discount: promoForm.discount,
      startDate: promoForm.startDate,
      endDate: promoForm.endDate,
      status: 'active',
      usageCount: 0,
      code: promoForm.code.toUpperCase()
    }]);
    setPromoForm({
      name: '',
      discount: '',
      startDate: '',
      endDate: '',
      code: ''
    });
    setShowPromoForm(false);
    success('Акция создана');
  };
  const handleDeletePromo = (id: number) => {
    setPromotions(prev => prev.filter(p => p.id !== id));
    success('Акция удалена');
  };
  const filteredMedia = mediaFilter === 'all' ? mediaItems : mediaItems.filter(m => m.type === mediaFilter);
  const faqCategories = [...new Set(faqItems.map(f => f.category))];
  const renderStars = (rating: number) => <div className="flex gap-0.5" data-id="element-10362">
      {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3 h-3 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-10363" />)}
    </div>;
  const backButton = (label: string, target: CmsView = 'dashboard') => <div className="flex items-center gap-4 mb-6" data-id="element-10364">
      <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" data-id="element-10366" />} onClick={() => setCurrentView(target)} data-id="element-10365">
        Назад
      </Button>
      <h2 className="text-xl font-bold text-slate-900" data-id="element-10367">{label}</h2>
    </div>;
  // ==================== DASHBOARD ====================
  const renderDashboard = () => <div className="space-y-8" data-id="element-10368">
      <div data-id="element-10369">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-10370">Контент</h2>
        <p className="text-slate-500 mt-1" data-id="element-10371">
          Управление контентом сайта и маркетинговыми материалами
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10372">
        <Card hoverable padding="md" onClick={() => setCurrentView('pages')} className="flex flex-col" data-id="element-10373">
          <div className="flex justify-between items-start mb-4" data-id="element-10374">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-10375">
              <FileText className="w-5 h-5" data-id="element-10376" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-10377">
              {pages.length} страниц
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10378">Страницы</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-10379">
            Управление страницами сайта: главная, о нас, контакты
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('blog')} className="flex flex-col" data-id="element-10380">
          <div className="flex justify-between items-start mb-4" data-id="element-10381">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600" data-id="element-10382">
              <BookOpen className="w-5 h-5" data-id="element-10383" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-10384">
              {blogPosts.length} статей
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10385">Блог</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-10386">
            Публикация статей, новостей и полезных материалов
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('media')} className="flex flex-col" data-id="element-10387">
          <div className="flex justify-between items-start mb-4" data-id="element-10388">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600" data-id="element-10389">
              <ImageIcon className="w-5 h-5" data-id="element-10390" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-10391">
              {mediaItems.length} файлов
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10392">Медиа</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-10393">
            Библиотека изображений, видео и документов
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('reviews')} className="flex flex-col" data-id="element-10394">
          <div className="flex justify-between items-start mb-4" data-id="element-10395">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-10396">
              <Star className="w-5 h-5" data-id="element-10397" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-10398">
              {cmsReviews.length} отзывов
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10399">Отзывы</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-10400">
            Модерация и публикация отзывов клиентов
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('faq')} className="flex flex-col" data-id="element-10401">
          <div className="flex justify-between items-start mb-4" data-id="element-10402">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600" data-id="element-10403">
              <HelpCircle className="w-5 h-5" data-id="element-10404" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-10405">
              {faqItems.length} вопросов
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10406">FAQ</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-10407">
            Управление часто задаваемыми вопросами
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('promotions')} className="flex flex-col" data-id="element-10408">
          <div className="flex justify-between items-start mb-4" data-id="element-10409">
            <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600" data-id="element-10410">
              <Megaphone className="w-5 h-5" data-id="element-10411" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-10412">
              {promotions.filter(p => p.status === 'active').length} активных
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10413">Акции</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-10414">
            Создание и управление промо-акциями и скидками
          </p>
        </Card>
      </div>

      <div className="pt-4" data-id="element-10415">
        <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-10416">
          Обзор контента
        </h3>
        <p className="text-sm text-slate-500 mb-4" data-id="element-10417">
          Общая статистика по всем разделам
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-10418">
          <Card padding="md" data-id="element-10419">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-10420">
              {pages.length + blogPosts.length + faqItems.length + cmsReviews.length + mediaItems.length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-10421">
              Всего элементов контента
            </div>
          </Card>
          <Card padding="md" data-id="element-10422">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-10423">
              {blogPosts.filter(p => p.status === 'published').length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-10424">Опубликовано статей</div>
          </Card>
          <Card padding="md" data-id="element-10425">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-10426">
              {blogPosts.filter(p => p.status === 'draft').length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-10427">Черновики</div>
          </Card>
          <Card padding="md" data-id="element-10428">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-10429">
              {cmsReviews.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-10430">Ожидают модерации</div>
          </Card>
        </div>
      </div>
    </div>;
  // ==================== PAGES LIST ====================
  const renderPagesList = () => <div className="space-y-6" data-id="element-10431">
      {backButton('Страницы сайта')}
      <p className="text-sm text-slate-500 -mt-4" data-id="element-10432">
        Управление контентом {pages.length} страниц сайта
      </p>

      <div className="flex gap-2 flex-wrap" data-id="element-10433">
        <button onClick={() => setPagesFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${pagesFilter === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-10434">
          Все ({pages.length})
        </button>
        {pageGroups.map(group => <button key={group} onClick={() => setPagesFilter(group)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${pagesFilter === group ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-10435">
            {group} ({pages.filter(p => p.group === group).length})
          </button>)}
      </div>

      <Card padding="none" className="overflow-hidden" data-id="element-10436">
        <table className="w-full text-left" data-id="element-10437">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500" data-id="element-10438">
            <tr data-id="element-10439">
              <th className="px-4 py-3 font-medium" data-id="element-10440">Название</th>
              <th className="px-4 py-3 font-medium" data-id="element-10441">URL</th>
              <th className="px-4 py-3 font-medium" data-id="element-10442">Группа</th>
              <th className="px-4 py-3 font-medium" data-id="element-10443">Статус</th>
              <th className="px-4 py-3 font-medium" data-id="element-10444">Блоков</th>
              <th className="px-4 py-3 font-medium" data-id="element-10445">Изменено</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-10446">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-10447">
            {filteredPages.map(page => <tr key={page.id} className="hover:bg-slate-50 transition-colors" data-id="element-10448">
                <td className="px-4 py-3 font-medium text-slate-900" data-id="element-10449">
                  {page.title}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono text-xs" data-id="element-10450">
                  {page.slug}
                </td>
                <td className="px-4 py-3" data-id="element-10451">
                  <Badge variant="neutral" size="sm" data-id="element-10452">
                    {page.group}
                  </Badge>
                </td>
                <td className="px-4 py-3" data-id="element-10453">
                  <Badge variant={page.status === 'published' ? 'success' : 'warning'} size="sm" data-id="element-10454">
                    {page.status === 'published' ? 'Опубликовано' : 'Черновик'}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-10455">
                  {(pageSections[page.id] || []).length}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-10456">
                  {page.lastModified}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-10457">
                  <div className="flex justify-end gap-2" data-id="element-10458">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" icon={<Eye className="w-4 h-4" data-id="element-10460" />} onClick={() => onNavigate(page.id)} title="Просмотр" data-id="element-10459" />
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-indigo-600" icon={<Edit2 className="w-4 h-4" data-id="element-10462" />} onClick={() => {
                  setSelectedPage(page);
                  setCurrentView('page_editor');
                }} title="Редактировать" data-id="element-10461" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </div>;
  // ==================== PAGE EDITOR ====================
  const renderPageEditor = () => <div className="space-y-6" data-id="element-10463">
      <div className="flex items-center justify-between" data-id="element-10464">
        <div className="flex items-center gap-4" data-id="element-10465">
          <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" data-id="element-10467" />} onClick={() => setCurrentView('pages')} data-id="element-10466">
            Назад
          </Button>
          <div data-id="element-10468">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-10469">
              Редактирование: {selectedPage?.title}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-10470">
              {selectedPage?.slug} · {currentSections.length} блоков
            </p>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-10471">
          <Button variant="outline" icon={<Eye className="w-4 h-4" data-id="element-10473" />} onClick={() => onNavigate(selectedPage?.id || 'home')} data-id="element-10472">
            Предпросмотр
          </Button>
          <Button variant="primary" icon={<Save className="w-4 h-4" data-id="element-10475" />} onClick={() => success('Изменения сохранены')} data-id="element-10474">
            Сохранить
          </Button>
        </div>
      </div>

      <div className="space-y-4" data-id="element-10476">
        {currentSections.map((section, index) => <Card key={section.id} padding="md" className={`transition-opacity ${section.visible ? 'opacity-100' : 'opacity-50'}`} data-id="element-10477">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100" data-id="element-10478">
              <div className="flex items-center gap-3" data-id="element-10479">
                <div className="flex flex-col gap-0.5" data-id="element-10480">
                  <button onClick={() => handleMoveSection(index, 'up')} disabled={index === 0} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30 transition-colors" data-id="element-10481">
                    <ChevronUp className="w-4 h-4" data-id="element-10482" />
                  </button>
                  <button onClick={() => handleMoveSection(index, 'down')} disabled={index === currentSections.length - 1} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30 transition-colors" data-id="element-10483">
                    <ChevronDown className="w-4 h-4" data-id="element-10484" />
                  </button>
                </div>
                <h3 className="text-base font-bold text-slate-900" data-id="element-10485">
                  Блок: {section.name}
                </h3>
                {!section.visible && <Badge variant="neutral" size="sm" data-id="element-10486">
                    Скрыт
                  </Badge>}
              </div>
              <div className="flex items-center gap-1" data-id="element-10487">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" icon={section.visible ? <EyeOff className="w-4 h-4" data-id="element-10489" /> : <Eye className="w-4 h-4" data-id="element-10490" />} onClick={() => handleToggleVisibility(index)} title={section.visible ? 'Скрыть' : 'Показать'} data-id="element-10488" />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-500" icon={<Trash2 className="w-4 h-4" data-id="element-10492" />} onClick={() => handleDeleteSection(index)} title="Удалить" data-id="element-10491" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10493">
              <div className="space-y-4" data-id="element-10494">
                <Input label="Заголовок" value={section.title} onChange={e => handleSectionChange(index, 'title', e.target.value)} data-id="element-10495" />
                <div data-id="element-10496">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10497">
                    Описание / Текст
                  </label>
                  <textarea className="w-full rounded-xl border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[100px] text-sm" value={section.description} onChange={e => handleSectionChange(index, 'description', e.target.value)} data-id="element-10498" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-10499">
                <Input label="Текст кнопки" value={section.buttonText} onChange={e => handleSectionChange(index, 'buttonText', e.target.value)} placeholder="Оставьте пустым, если кнопка не нужна" data-id="element-10500" />
                <ImageUpload label="Изображение блока" value={section.imageUrl} onChange={url => handleSectionChange(index, 'imageUrl', url)} height="h-28" data-id="element-10501" />
              </div>
            </div>
          </Card>)}
        <button onClick={handleAddSection} className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all font-medium text-sm" data-id="element-10502">
          <Plus className="w-4 h-4" data-id="element-10503" /> Добавить блок
        </button>
      </div>
    </div>;
  // ==================== BLOG LIST ====================
  const renderBlogList = () => {
    const publishedCount = blogPosts.filter(p => p.status === 'published').length;
    const draftCount = blogPosts.filter(p => p.status === 'draft').length;
    const totalViews = blogPosts.reduce((sum, p) => sum + p.views, 0);
    return <div className="space-y-6" data-id="element-10504">
        <div className="flex items-center justify-between" data-id="element-10505">
          {backButton('Блог')}
          <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-10507" />} onClick={() => openPostEditor(null)} data-id="element-10506">
            Создать статью
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-10508">
          <Card padding="sm" data-id="element-10509">
            <div className="text-2xl font-bold text-slate-900" data-id="element-10510">
              {blogPosts.length}
            </div>
            <div className="text-xs text-slate-500" data-id="element-10511">Всего статей</div>
          </Card>
          <Card padding="sm" data-id="element-10512">
            <div className="text-2xl font-bold text-emerald-600" data-id="element-10513">
              {publishedCount}
            </div>
            <div className="text-xs text-slate-500" data-id="element-10514">Опубликовано</div>
          </Card>
          <Card padding="sm" data-id="element-10515">
            <div className="text-2xl font-bold text-amber-600" data-id="element-10516">
              {draftCount}
            </div>
            <div className="text-xs text-slate-500" data-id="element-10517">Черновики</div>
          </Card>
          <Card padding="sm" data-id="element-10518">
            <div className="text-2xl font-bold text-indigo-600" data-id="element-10519">
              {totalViews.toLocaleString()}
            </div>
            <div className="text-xs text-slate-500" data-id="element-10520">Просмотров</div>
          </Card>
        </div>

        {/* Search & Filters */}
        <div className="space-y-3" data-id="element-10521">
          <Input icon={<Search className="w-4 h-4" data-id="element-10523" />} value={blogSearch} onChange={e => setBlogSearch(e.target.value)} placeholder="Поиск по заголовку или описанию..." data-id="element-10522" />
          <div className="flex flex-wrap gap-2" data-id="element-10524">
            <div className="flex gap-1 flex-wrap" data-id="element-10525">
              <button onClick={() => setBlogStatusFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogStatusFilter === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-10526">
                Все ({blogPosts.length})
              </button>
              <button onClick={() => setBlogStatusFilter('published')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogStatusFilter === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-10527">
                Опубликовано ({publishedCount})
              </button>
              <button onClick={() => setBlogStatusFilter('draft')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogStatusFilter === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-10528">
                Черновики ({draftCount})
              </button>
            </div>
            <div className="h-6 w-px bg-slate-200 hidden sm:block self-center" data-id="element-10529" />
            <div className="flex gap-1 flex-wrap items-center" data-id="element-10530">
              <button onClick={() => setBlogCategoryFilter('all')} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogCategoryFilter === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-10531">
                Все категории
              </button>
              {blogCategories.map(cat => <button key={cat} onClick={() => setBlogCategoryFilter(cat)} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogCategoryFilter === cat ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} group relative`} data-id="element-10532">
                  {cat}
                  <span onClick={e => {
                e.stopPropagation();
                removeBlogCategory(cat);
              }} className="ml-1 text-slate-400 hover:text-rose-500 hidden group-hover:inline" title="Удалить категорию" data-id="element-10533">
                    ×
                  </span>
                </button>)}
              {showNewCategoryInput ? <div className="flex items-center gap-1" data-id="element-10534">
                  <input autoFocus value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') addBlogCategory(newCategoryInput);
                if (e.key === 'Escape') {
                  setShowNewCategoryInput(false);
                  setNewCategoryInput('');
                }
              }} placeholder="Название..." className="px-2 py-1 rounded-lg border border-slate-200 text-xs w-28 outline-none focus:border-indigo-400" data-id="element-10535" />
                  <button onClick={() => addBlogCategory(newCategoryInput)} className="px-2 py-1 rounded-lg bg-indigo-100 text-indigo-700 text-xs font-medium hover:bg-indigo-200" data-id="element-10536">
                    OK
                  </button>
                  <button onClick={() => {
                setShowNewCategoryInput(false);
                setNewCategoryInput('');
              }} className="px-1.5 py-1 rounded-lg text-slate-400 hover:text-slate-600 text-xs" data-id="element-10537">
                    ✕
                  </button>
                </div> : <button onClick={() => setShowNewCategoryInput(true)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors" data-id="element-10538">
                  + Категория
                </button>}
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredBlogPosts.length > 0 ? <Card padding="none" className="overflow-hidden" data-id="element-10539">
            <table className="w-full text-left" data-id="element-10540">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500" data-id="element-10541">
                <tr data-id="element-10542">
                  <th className="px-4 py-3 font-medium" data-id="element-10543">Заголовок</th>
                  <th className="px-4 py-3 font-medium" data-id="element-10544">Категория</th>
                  <th className="px-4 py-3 font-medium" data-id="element-10545">Статус</th>
                  <th className="px-4 py-3 font-medium" data-id="element-10546">Дата</th>
                  <th className="px-4 py-3 font-medium" data-id="element-10547">Просмотры</th>
                  <th className="px-4 py-3 text-right font-medium" data-id="element-10548">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-10549">
                {filteredBlogPosts.map(post => <tr key={post.id} className="hover:bg-slate-50 transition-colors" data-id="element-10550">
                    <td className="px-4 py-3" data-id="element-10551">
                      <div className="flex items-center gap-3" data-id="element-10552">
                        {post.imageUrl && <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0" data-id="element-10553">
                            <img src={post.imageUrl} alt="" className="w-full h-full object-cover" data-id="element-10554" />
                          </div>}
                        <div data-id="element-10555">
                          <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5" data-id="element-10556">
                            {post.featured && <Pin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" data-id="element-10557" />}
                            {post.title}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 line-clamp-1" data-id="element-10558">
                            {post.excerpt}
                          </div>
                          {post.tags && post.tags.length > 0 && <div className="flex gap-1 mt-1 flex-wrap" data-id="element-10559">
                              {post.tags.slice(0, 3).map(tag => <span key={tag} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px]" data-id="element-10560">
                                  {tag}
                                </span>)}
                              {post.tags.length > 3 && <span className="text-[10px] text-slate-400" data-id="element-10561">
                                  +{post.tags.length - 3}
                                </span>}
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-10562">
                      <Badge variant="neutral" size="sm" data-id="element-10563">
                        {post.category}
                      </Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-10564">
                      <button onClick={() => handleTogglePostStatus(post.id)} title={post.status === 'published' ? 'Нажмите, чтобы снять с публикации' : 'Нажмите, чтобы опубликовать'} data-id="element-10565">
                        <Badge variant={post.status === 'published' ? 'success' : 'warning'} size="sm" className="cursor-pointer hover:opacity-80 transition-opacity" data-id="element-10566">
                          {post.status === 'published' ? 'Опубликовано' : 'Черновик'}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500" data-id="element-10567">
                      {post.date}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500" data-id="element-10568">
                      {post.views.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-10569">
                      <div className="flex justify-end gap-1" data-id="element-10570">
                        <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${post.featured ? 'text-amber-500' : 'text-slate-400 hover:text-amber-500'}`} icon={<Pin className="w-3.5 h-3.5" data-id="element-10572" />} onClick={() => {
                    setBlogPosts(prev => prev.map(p => p.id === post.id ? {
                      ...p,
                      featured: !p.featured
                    } : {
                      ...p,
                      featured: false
                    }));
                    success(post.featured ? 'Статья откреплена' : 'Статья закреплена в топе блога');
                  }} title={post.featured ? 'Открепить' : 'Закрепить в топе'} data-id="element-10571" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-indigo-600" icon={<Edit2 className="w-3.5 h-3.5" data-id="element-10574" />} onClick={() => openPostEditor(post)} title="Редактировать" data-id="element-10573" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" icon={<Copy className="w-3.5 h-3.5" data-id="element-10576" />} onClick={() => handleDuplicatePost(post)} title="Дублировать" data-id="element-10575" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" icon={<Trash2 className="w-3.5 h-3.5" data-id="element-10578" />} onClick={() => handleDeletePost(post.id)} title="Удалить" data-id="element-10577" />
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </Card> : <Card padding="lg" className="text-center" data-id="element-10579">
            <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-10580" />
            <p className="text-sm text-slate-500 mb-1" data-id="element-10581">
              {blogSearch || blogCategoryFilter !== 'all' || blogStatusFilter !== 'all' ? 'Статьи не найдены' : 'Пока нет статей'}
            </p>
            <p className="text-xs text-slate-400" data-id="element-10582">
              {blogSearch || blogCategoryFilter !== 'all' || blogStatusFilter !== 'all' ? 'Попробуйте изменить фильтры' : 'Создайте первую статью'}
            </p>
          </Card>}
      </div>;
  };
  // ==================== BLOG EDITOR ====================
  const insertFormat = (prefix: string, suffix: string = '', placeholder: string = '') => {
    const ta = contentRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = postForm.content;
    const selected = text.substring(start, end);
    const insert = selected || placeholder;
    const newText = text.substring(0, start) + prefix + insert + suffix + text.substring(end);
    setPostForm(p => ({
      ...p,
      content: newText
    }));
    setTimeout(() => {
      ta.focus();
      const cursorPos = start + prefix.length + insert.length + suffix.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };
  const insertLinePrefix = (prefix: string) => {
    const ta = contentRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const text = postForm.content;
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const newText = text.substring(0, lineStart) + prefix + text.substring(lineStart);
    setPostForm(p => ({
      ...p,
      content: newText
    }));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length);
    }, 0);
  };
  const tocHeadings = postForm.content.split('\n').filter(line => line.startsWith('## ') || line.startsWith('### ')).map(line => ({
    level: line.startsWith('### ') ? 3 : 2,
    text: line.replace(/^#{2,3}\s+/, '')
  }));
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !postForm.tags.includes(tag)) {
      setPostForm(p => ({
        ...p,
        tags: [...p.tags, tag]
      }));
    }
    setTagInput('');
  };
  const handleRemoveTag = (tag: string) => {
    setPostForm(p => ({
      ...p,
      tags: p.tags.filter(t => t !== tag)
    }));
  };
  const toolbarBtnClass = 'w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors text-xs font-bold';
  const renderBlogEditor = () => <div className="space-y-6" data-id="element-10583">
      <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-10584">
        <div className="flex items-center gap-4" data-id="element-10585">
          <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" data-id="element-10587" />} onClick={() => setCurrentView('blog')} data-id="element-10586">
            Назад
          </Button>
          <h2 className="text-xl font-bold text-slate-900" data-id="element-10588">
            {selectedPost ? 'Редактировать статью' : 'Новая статья'}
          </h2>
        </div>
        <div className="flex gap-3" data-id="element-10589">
          {selectedPost && <Button variant="outline" icon={<Eye className="w-4 h-4" data-id="element-10591" />} onClick={() => onNavigate(`blog-post/cms-${selectedPost.id}`)} data-id="element-10590">
              Предпросмотр
            </Button>}
          <Button variant="outline" icon={<Save className="w-4 h-4" data-id="element-10593" />} onClick={handleSavePost} data-id="element-10592">
            Сохранить черновик
          </Button>
          <Button variant="primary" icon={<CheckCircle2 className="w-4 h-4" data-id="element-10595" />} onClick={() => handleSavePost('published')} data-id="element-10594">
            Опубликовать
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-10596">
        <div className="lg:col-span-2 space-y-4" data-id="element-10597">
          <Card padding="md" data-id="element-10598">
            <div className="space-y-4" data-id="element-10599">
              <Input label="Заголовок" value={postForm.title} onChange={e => setPostForm(p => ({
              ...p,
              title: e.target.value
            }))} placeholder="Введите заголовок статьи" data-id="element-10600" />
              <div data-id="element-10601">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10602">
                  Краткое описание
                </label>
                <textarea className="w-full rounded-xl border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[80px] text-sm" value={postForm.excerpt} onChange={e => setPostForm(p => ({
                ...p,
                excerpt: e.target.value
              }))} placeholder="Краткое описание для превью..." data-id="element-10603" />
              </div>
              <div data-id="element-10604">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10605">
                  Содержание статьи
                </label>
                {/* Formatting Toolbar */}
                <div className="flex items-center gap-0.5 p-1.5 bg-slate-50 border border-slate-200 border-b-0 rounded-t-xl flex-wrap" data-id="element-10606">
                  <button title="Жирный (Ctrl+B)" className={toolbarBtnClass} onClick={() => insertFormat('**', '**', 'жирный текст')} data-id="element-10607">
                    <span className="font-black text-sm" data-id="element-10608">B</span>
                  </button>
                  <button title="Курсив (Ctrl+I)" className={toolbarBtnClass} onClick={() => insertFormat('*', '*', 'курсив')} data-id="element-10609">
                    <span className="italic text-sm font-serif" data-id="element-10610">I</span>
                  </button>
                  <div className="w-px h-5 bg-slate-200 mx-1" data-id="element-10611" />
                  <button title="Заголовок H2" className={toolbarBtnClass} onClick={() => insertLinePrefix('## ')} data-id="element-10612">
                    <span className="text-[11px] font-black" data-id="element-10613">H2</span>
                  </button>
                  <button title="Заголовок H3" className={toolbarBtnClass} onClick={() => insertLinePrefix('### ')} data-id="element-10614">
                    <span className="text-[11px] font-black" data-id="element-10615">H3</span>
                  </button>
                  <div className="w-px h-5 bg-slate-200 mx-1" data-id="element-10616" />
                  <button title="Маркированный список" className={toolbarBtnClass} onClick={() => insertLinePrefix('- ')} data-id="element-10617">
                    <span className="text-sm" data-id="element-10618">•</span>
                  </button>
                  <button title="Нумерованный список" className={toolbarBtnClass} onClick={() => insertLinePrefix('1. ')} data-id="element-10619">
                    <span className="text-[11px] font-bold" data-id="element-10620">1.</span>
                  </button>
                  <button title="Цитата" className={toolbarBtnClass} onClick={() => insertLinePrefix('> ')} data-id="element-10621">
                    <span className="text-sm font-serif" data-id="element-10622">"</span>
                  </button>
                  <div className="w-px h-5 bg-slate-200 mx-1" data-id="element-10623" />
                  <button title="Ссылка" className={toolbarBtnClass} onClick={() => insertFormat('[', '](url)', 'текст ссылки')} data-id="element-10624">
                    <ExternalLink className="w-3.5 h-3.5" data-id="element-10625" />
                  </button>
                  <button title="Разделитель" className={toolbarBtnClass} onClick={() => insertFormat('\n\n---\n\n', '', '')} data-id="element-10626">
                    <span className="text-[10px] tracking-widest" data-id="element-10627">———</span>
                  </button>
                </div>
                <textarea ref={contentRef} className="w-full rounded-b-xl rounded-t-none border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[320px] text-sm font-mono leading-relaxed" value={postForm.content} onChange={e => setPostForm(p => ({
                ...p,
                content: e.target.value
              }))} placeholder="Используйте панель форматирования выше или Markdown-разметку:&#10;&#10;## Заголовок раздела&#10;### Подзаголовок&#10;&#10;Обычный текст. **Жирный** и *курсив*.&#10;&#10;- Пункт списка&#10;- Ещё пункт&#10;&#10;> Цитата&#10;&#10;[Ссылка](https://example.com)" onKeyDown={e => {
                if (e.ctrlKey || e.metaKey) {
                  if (e.key === 'b') {
                    e.preventDefault();
                    insertFormat('**', '**', 'жирный текст');
                  }
                  if (e.key === 'i') {
                    e.preventDefault();
                    insertFormat('*', '*', 'курсив');
                  }
                }
              }} data-id="element-10628" />
                <div className="flex items-center justify-between mt-1.5" data-id="element-10629">
                  <span className="text-[10px] text-slate-400" data-id="element-10630">
                    Поддерживается Markdown-разметка • Ctrl+B — жирный • Ctrl+I
                    — курсив
                  </span>
                  <span className="text-[10px] text-slate-400" data-id="element-10631">
                    {postForm.content.length} символов
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card padding="md" data-id="element-10632">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-10633">
              <Search className="w-4 h-4 text-slate-400" data-id="element-10634" /> SEO настройки
            </h3>
            <div className="space-y-4" data-id="element-10635">
              <Input label="Meta Title" value={postForm.seoTitle} onChange={e => setPostForm(p => ({
              ...p,
              seoTitle: e.target.value
            }))} placeholder="Заголовок для поисковых систем" data-id="element-10636" />
              <div data-id="element-10637">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10638">
                  Meta Description
                </label>
                <textarea className="w-full rounded-xl border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[60px] text-sm" value={postForm.seoDescription} onChange={e => setPostForm(p => ({
                ...p,
                seoDescription: e.target.value
              }))} placeholder="Описание для поисковых систем (до 160 символов)" data-id="element-10639" />
              </div>
              <Input label="Ключевые слова" value={postForm.seoKeywords} onChange={e => setPostForm(p => ({
              ...p,
              seoKeywords: e.target.value
            }))} placeholder="ключевое слово 1, ключевое слово 2" data-id="element-10640" />
            </div>
          </Card>
        </div>

        <div className="space-y-4" data-id="element-10641">
          <Card padding="md" data-id="element-10642">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-10643">Параметры</h3>
            <div className="space-y-4" data-id="element-10644">
              <div data-id="element-10645">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10646">
                  Категория
                </label>
                <div className="flex gap-2" data-id="element-10647">
                  <select className="flex-1 rounded-xl border-slate-200 shadow-sm px-3 py-2.5 border outline-none text-sm focus:border-indigo-500" value={postForm.category} onChange={e => {
                  if (e.target.value === '__new__') {
                    setShowNewCategoryInput(true);
                  } else {
                    setPostForm(p => ({
                      ...p,
                      category: e.target.value
                    }));
                  }
                }} data-id="element-10648">
                    {blogCategories.map(cat => <option key={cat} value={cat} data-id="element-10649">
                        {cat}
                      </option>)}
                    <option value="__new__" data-id="element-10650">+ Новая категория...</option>
                  </select>
                </div>
                {showNewCategoryInput && <div className="flex gap-2 mt-2" data-id="element-10651">
                    <input autoFocus value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && newCategoryInput.trim()) {
                    addBlogCategory(newCategoryInput);
                    setPostForm(p => ({
                      ...p,
                      category: newCategoryInput.trim()
                    }));
                  }
                  if (e.key === 'Escape') {
                    setShowNewCategoryInput(false);
                    setNewCategoryInput('');
                  }
                }} placeholder="Название категории..." className="flex-1 rounded-xl border-slate-200 shadow-sm px-3 py-2 border outline-none text-sm focus:border-indigo-500" data-id="element-10652" />
                    <button onClick={() => {
                  if (newCategoryInput.trim()) {
                    addBlogCategory(newCategoryInput);
                    setPostForm(p => ({
                      ...p,
                      category: newCategoryInput.trim()
                    }));
                  }
                }} className="px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-10653">
                      Добавить
                    </button>
                    <button onClick={() => {
                  setShowNewCategoryInput(false);
                  setNewCategoryInput('');
                }} className="px-2 py-2 rounded-xl text-slate-400 hover:text-slate-600 text-sm" data-id="element-10654">
                      ✕
                    </button>
                  </div>}
              </div>
              <div data-id="element-10655">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10656">
                  Статус
                </label>
                <select className="w-full rounded-xl border-slate-200 shadow-sm px-3 py-2.5 border outline-none text-sm focus:border-indigo-500" value={postForm.status} onChange={e => setPostForm(p => ({
                ...p,
                status: e.target.value
              }))} data-id="element-10657">
                  <option value="draft" data-id="element-10658">Черновик</option>
                  <option value="published" data-id="element-10659">Опубликовано</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Tags */}
          <Card padding="md" data-id="element-10660">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-10661">
              <Tag className="w-4 h-4 text-slate-400" data-id="element-10662" /> Теги
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-10663">
              {postForm.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium" data-id="element-10664">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-indigo-900 transition-colors" data-id="element-10665">
                    <X className="w-3 h-3" data-id="element-10666" />
                  </button>
                </span>)}
              {postForm.tags.length === 0 && <span className="text-xs text-slate-400" data-id="element-10667">Нет тегов</span>}
            </div>
            <div className="flex gap-2" data-id="element-10668">
              <input className="flex-1 rounded-lg border-slate-200 shadow-sm px-3 py-1.5 border outline-none text-sm focus:border-indigo-500" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }} placeholder="Новый тег..." data-id="element-10669" />
              <Button variant="outline" size="sm" onClick={handleAddTag} icon={<Plus className="w-3.5 h-3.5" data-id="element-10671" />} data-id="element-10670">
                Добавить
              </Button>
            </div>
          </Card>

          {/* Table of Contents */}
          <Card padding="md" data-id="element-10672">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-10673">
              <FileText className="w-4 h-4 text-slate-400" data-id="element-10674" /> Содержание
            </h3>
            {tocHeadings.length > 0 ? <div className="space-y-1" data-id="element-10675">
                {tocHeadings.map((h, i) => <div key={i} className={`text-sm text-slate-600 py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors cursor-default ${h.level === 3 ? 'pl-6 text-xs text-slate-500' : 'font-medium'}`} data-id="element-10676">
                    <span className="text-slate-300 mr-2" data-id="element-10677">
                      {h.level === 2 ? '§' : '∟'}
                    </span>
                    {h.text}
                  </div>)}
              </div> : <div className="text-center py-4" data-id="element-10678">
                <p className="text-xs text-slate-400 mb-1" data-id="element-10679">Содержание пусто</p>
                <p className="text-[10px] text-slate-300" data-id="element-10680">
                  Добавьте заголовки (## или ###) в текст статьи
                </p>
              </div>}
            <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-10681">
              <p className="text-[10px] text-slate-400 leading-relaxed" data-id="element-10682">
                Содержание формируется автоматически из заголовков H2 и H3 в
                тексте статьи. Используйте кнопки H2/H3 на панели
                форматирования.
              </p>
            </div>
          </Card>

          <Card padding="md" data-id="element-10683">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-10684">Обложка</h3>
            <ImageUpload label="" value={postForm.imageUrl} onChange={url => setPostForm(p => ({
            ...p,
            imageUrl: url
          }))} height="h-36" data-id="element-10685" />
          </Card>
        </div>
      </div>
    </div>;
  // ==================== MEDIA LIBRARY ====================
  const renderMediaLibrary = () => {
    const getTypeIcon = (type: string) => {
      switch (type) {
        case 'image':
          return <FileImage className="w-5 h-5 text-blue-500" data-id="element-10686" />;
        case 'video':
          return <FileVideo className="w-5 h-5 text-rose-500" data-id="element-10687" />;
        case 'document':
          return <File className="w-5 h-5 text-amber-500" data-id="element-10688" />;
        default:
          return <File className="w-5 h-5 text-slate-400" data-id="element-10689" />;
      }
    };
    const mediaInputRef = useRef<HTMLInputElement>(null);
    const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType: 'image' | 'video' | 'document' = isImage ? 'image' : isVideo ? 'video' : 'document';
      const colors = {
        image: 'bg-blue-100',
        video: 'bg-rose-100',
        document: 'bg-amber-100'
      };
      const reader = new FileReader();
      reader.onload = ev => {
        const url = ev.target?.result as string;
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        setMediaItems(prev => [{
          id: Date.now(),
          name: file.name,
          type: fileType,
          size: `${sizeMB} MB`,
          date: 'Сегодня',
          url,
          color: colors[fileType]
        }, ...prev]);
        success('Файл загружен');
      };
      reader.readAsDataURL(file);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    };
    return <div className="space-y-6" data-id="element-10690">
        <input ref={mediaInputRef} type="file" accept="image/*,video/*,.pdf,.doc,.docx" onChange={handleMediaUpload} className="hidden" data-id="element-10691" />
        <div className="flex items-center justify-between" data-id="element-10692">
          {backButton('Медиа библиотека')}
          <Button variant="primary" size="sm" icon={<Upload className="w-4 h-4" data-id="element-10694" />} onClick={() => mediaInputRef.current?.click()} data-id="element-10693">
            Загрузить
          </Button>
        </div>

        <div className="flex gap-2" data-id="element-10695">
          {([['all', 'Все'], ['image', 'Изображения'], ['video', 'Видео'], ['document', 'Документы']] as const).map(([key, label]) => <button key={key} onClick={() => setMediaFilter(key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${mediaFilter === key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-10696">
              {label}
            </button>)}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-10697">
          {filteredMedia.map(item => <Card key={item.id} padding="sm" className="group relative" data-id="element-10698">
              <div className={`h-28 rounded-lg ${item.color} flex items-center justify-center mb-3 overflow-hidden`} data-id="element-10699">
                {item.type === 'image' && item.url.startsWith('data:') ? <img src={item.url} alt={item.name} className="w-full h-full object-cover" data-id="element-10700" /> : getTypeIcon(item.type)}
              </div>
              <div className="text-sm font-medium text-slate-900 truncate" data-id="element-10701">
                {item.name}
              </div>
              <div className="flex justify-between items-center mt-1" data-id="element-10702">
                <span className="text-xs text-slate-500" data-id="element-10703">{item.size}</span>
                <span className="text-xs text-slate-400" data-id="element-10704">{item.date}</span>
              </div>
              <div className="flex gap-1 mt-3" data-id="element-10705">
                <Button variant="ghost" size="sm" className="h-7 flex-1 text-xs" icon={<Copy className="w-3 h-3" data-id="element-10707" />} onClick={() => {
              navigator.clipboard?.writeText(item.url);
              success('URL скопирован');
            }} data-id="element-10706">
                  URL
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" icon={<Trash2 className="w-3 h-3" data-id="element-10709" />} onClick={() => handleDeleteMedia(item.id)} data-id="element-10708" />
              </div>
            </Card>)}
        </div>

        {filteredMedia.length === 0 && <div className="text-center py-16 text-slate-400" data-id="element-10710">
            <ImageIcon className="w-10 h-10 mx-auto mb-3" data-id="element-10711" />
            <p className="text-sm" data-id="element-10712">Нет файлов в этой категории</p>
          </div>}
      </div>;
  };
  // ==================== REVIEWS ====================
  const renderReviewsManager = () => <div className="space-y-6" data-id="element-10713">
      {backButton('Отзывы')}
      <div className="flex gap-3 -mt-4 mb-2" data-id="element-10714">
        <Badge variant={undefined} size="sm" className="bg-amber-50 text-amber-700 border border-amber-200" data-id="element-10715">
          {cmsReviews.filter(r => r.status === 'pending').length} на модерации
        </Badge>
        <Badge variant={undefined} size="sm" className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-10716">
          {cmsReviews.filter(r => r.status === 'published').length}{' '}
          опубликовано
        </Badge>
        <Badge variant={undefined} size="sm" className="bg-slate-50 text-slate-600 border border-slate-200" data-id="element-10717">
          {cmsReviews.filter(r => r.status === 'rejected').length} отклонено
        </Badge>
      </div>
      <Card padding="none" className="overflow-hidden" data-id="element-10718">
        <table className="w-full text-left" data-id="element-10719">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500" data-id="element-10720">
            <tr data-id="element-10721">
              <th className="px-4 py-3 font-medium" data-id="element-10722">Автор</th>
              <th className="px-4 py-3 font-medium" data-id="element-10723">Рейтинг</th>
              <th className="px-4 py-3 font-medium" data-id="element-10724">Отзыв</th>
              <th className="px-4 py-3 font-medium" data-id="element-10725">Услуга</th>
              <th className="px-4 py-3 font-medium" data-id="element-10726">Дата</th>
              <th className="px-4 py-3 font-medium" data-id="element-10727">Статус</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-10728">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-10729">
            {cmsReviews.map(review => <tr key={review.id} className="hover:bg-slate-50 transition-colors" data-id="element-10730">
                <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-10731">
                  {review.author}
                </td>
                <td className="px-4 py-3" data-id="element-10732">{renderStars(review.rating)}</td>
                <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" data-id="element-10733">
                  {review.text}
                </td>
                <td className="px-4 py-3" data-id="element-10734">
                  <Badge variant="neutral" size="sm" data-id="element-10735">
                    {review.service}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-10736">
                  {review.date}
                </td>
                <td className="px-4 py-3" data-id="element-10737">
                  <Badge variant={review.status === 'published' ? 'success' : review.status === 'pending' ? 'warning' : 'error'} size="sm" data-id="element-10738">
                    {review.status === 'published' ? 'Опубликован' : review.status === 'pending' ? 'На модерации' : 'Отклонён'}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-10739">
                  <div className="flex justify-end gap-1" data-id="element-10740">
                    {review.status === 'pending' && <>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600" icon={<CheckCircle2 className="w-3.5 h-3.5" data-id="element-10742" />} onClick={() => handleReviewAction(review.id, 'publish')} title="Одобрить" data-id="element-10741" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" icon={<XCircle className="w-3.5 h-3.5" data-id="element-10744" />} onClick={() => handleReviewAction(review.id, 'reject')} title="Отклонить" data-id="element-10743" />
                      </>}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400" icon={<Trash2 className="w-3.5 h-3.5" data-id="element-10746" />} onClick={() => handleReviewAction(review.id, 'delete')} title="Удалить" data-id="element-10745" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </div>;
  // ==================== FAQ EDITOR ====================
  const renderFaqManager = () => <div className="space-y-6" data-id="element-10747">
      <div className="flex items-center justify-between" data-id="element-10748">
        {backButton('FAQ — Часто задаваемые вопросы')}
        <Button variant="primary" size="sm" icon={<Save className="w-4 h-4" data-id="element-10750" />} onClick={() => success('FAQ сохранён')} data-id="element-10749">
          Сохранить всё
        </Button>
      </div>

      {faqCategories.map(category => {
      const items = faqItems.filter(f => f.category === category);
      return <div key={category} data-id="element-10751">
            <div className="flex items-center justify-between mb-3" data-id="element-10752">
              <h3 className="text-base font-bold text-slate-900" data-id="element-10753">
                {category}{' '}
                <span className="text-slate-400 font-normal text-sm" data-id="element-10754">
                  ({items.length})
                </span>
              </h3>
              <Button variant="ghost" size="sm" className="h-7 text-xs" icon={<Plus className="w-3 h-3" data-id="element-10756" />} onClick={() => handleAddFaq(category)} data-id="element-10755">
                Добавить вопрос
              </Button>
            </div>
            <div className="space-y-3 mb-6" data-id="element-10757">
              {items.map(item => <Card key={item.id} padding="sm" className="relative" data-id="element-10758">
                  <div className="absolute top-3 right-3" data-id="element-10759">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-400 hover:text-rose-600" icon={<Trash2 className="w-3.5 h-3.5" data-id="element-10761" />} onClick={() => handleDeleteFaq(item.id)} data-id="element-10760" />
                  </div>
                  <div className="space-y-3 pr-10" data-id="element-10762">
                    <div data-id="element-10763">
                      <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-10764">
                        Вопрос
                      </label>
                      <input className="w-full rounded-lg border-slate-200 shadow-sm px-3 py-2 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm font-medium" value={item.question} onChange={e => handleFaqChange(item.id, 'question', e.target.value)} placeholder="Введите вопрос..." data-id="element-10765" />
                    </div>
                    <div data-id="element-10766">
                      <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-10767">
                        Ответ
                      </label>
                      <textarea className="w-full rounded-lg border-slate-200 shadow-sm px-3 py-2 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[60px] text-sm" value={item.answer} onChange={e => handleFaqChange(item.id, 'answer', e.target.value)} placeholder="Введите ответ..." data-id="element-10768" />
                    </div>
                  </div>
                </Card>)}
            </div>
          </div>;
    })}

      <button onClick={() => {
      const name = 'Новая категория';
      handleAddFaq(name);
    }} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all font-medium text-sm" data-id="element-10769">
        <Plus className="w-4 h-4" data-id="element-10770" /> Добавить категорию
      </button>
    </div>;
  // ==================== PROMOTIONS ====================
  const renderPromotionsManager = () => <div className="space-y-6" data-id="element-10771">
      <div className="flex items-center justify-between" data-id="element-10772">
        {backButton('Акции и промокоды')}
        <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-10774" />} onClick={() => setShowPromoForm(true)} data-id="element-10773">
          Создать акцию
        </Button>
      </div>

      {showPromoForm && <Card padding="md" className="border-indigo-200 bg-indigo-50/30" data-id="element-10775">
          <div className="flex items-center justify-between mb-4" data-id="element-10776">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-10777">Новая акция</h3>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" icon={<X className="w-4 h-4" data-id="element-10779" />} onClick={() => setShowPromoForm(false)} data-id="element-10778" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10780">
            <Input label="Название" value={promoForm.name} onChange={e => setPromoForm(p => ({
          ...p,
          name: e.target.value
        }))} placeholder="Весенняя скидка" data-id="element-10781" />
            <Input label="Промокод" value={promoForm.code} onChange={e => setPromoForm(p => ({
          ...p,
          code: e.target.value
        }))} placeholder="SPRING20" data-id="element-10782" />
            <Input label="Скидка" value={promoForm.discount} onChange={e => setPromoForm(p => ({
          ...p,
          discount: e.target.value
        }))} placeholder="20%" data-id="element-10783" />
            <Input label="Дата начала" value={promoForm.startDate} onChange={e => setPromoForm(p => ({
          ...p,
          startDate: e.target.value
        }))} placeholder="01.04.2026" data-id="element-10784" />
            <Input label="Дата окончания" value={promoForm.endDate} onChange={e => setPromoForm(p => ({
          ...p,
          endDate: e.target.value
        }))} placeholder="30.04.2026" data-id="element-10785" />
            <div className="flex items-end" data-id="element-10786">
              <Button variant="primary" className="w-full" onClick={handleCreatePromo} data-id="element-10787">
                Создать
              </Button>
            </div>
          </div>
        </Card>}

      <div className="space-y-3" data-id="element-10788">
        {promotions.map(promo => <Card key={promo.id} padding="md" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-10789">
            <div className="flex items-center gap-4" data-id="element-10790">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${promo.status === 'active' ? 'bg-emerald-50 text-emerald-600' : promo.status === 'scheduled' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-10791">
                <Tag className="w-5 h-5" data-id="element-10792" />
              </div>
              <div data-id="element-10793">
                <div className="font-medium text-slate-900" data-id="element-10794">{promo.name}</div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500" data-id="element-10795">
                  <span className="font-mono font-bold text-indigo-600" data-id="element-10796">
                    {promo.code}
                  </span>
                  <span data-id="element-10797">Скидка: {promo.discount}</span>
                  <span data-id="element-10798">
                    {promo.startDate} — {promo.endDate}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-10799">
              <div className="text-right" data-id="element-10800">
                <div className="text-sm font-bold text-slate-900" data-id="element-10801">
                  {promo.usageCount}
                </div>
                <div className="text-xs text-slate-500" data-id="element-10802">использований</div>
              </div>
              <Badge variant={promo.status === 'active' ? 'success' : promo.status === 'scheduled' ? 'warning' : 'neutral'} size="sm" data-id="element-10803">
                {promo.status === 'active' ? 'Активна' : promo.status === 'scheduled' ? 'Запланирована' : 'Истекла'}
              </Badge>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-400 hover:text-rose-600" icon={<Trash2 className="w-4 h-4" data-id="element-10805" />} onClick={() => handleDeletePromo(promo.id)} data-id="element-10804" />
            </div>
          </Card>)}
      </div>

      {promotions.length === 0 && <div className="text-center py-16 text-slate-400" data-id="element-10806">
          <Tag className="w-10 h-10 mx-auto mb-3" data-id="element-10807" />
          <p className="text-sm" data-id="element-10808">Нет активных акций</p>
        </div>}
    </div>;
  // ==================== MAIN RENDER ====================
  return <div className="max-w-6xl mx-auto" data-id="element-10809">
      {currentView === 'dashboard' && renderDashboard()}
      {currentView === 'pages' && renderPagesList()}
      {currentView === 'page_editor' && renderPageEditor()}
      {currentView === 'blog' && renderBlogList()}
      {currentView === 'blog_editor' && renderBlogEditor()}
      {currentView === 'media' && renderMediaLibrary()}
      {currentView === 'reviews' && renderReviewsManager()}
      {currentView === 'faq' && renderFaqManager()}
      {currentView === 'promotions' && renderPromotionsManager()}

      {/* Confirmation modal for category deletion */}
      {categoryToDelete && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCategoryToDelete(null)} data-id="element-10810">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-10811">
            <div className="flex items-center gap-3 mb-4" data-id="element-10812">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-10813">
                <Trash2 className="w-5 h-5 text-rose-600" data-id="element-10814" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" data-id="element-10815">
                Удалить категорию?
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-2" data-id="element-10816">
              Вы удаляете категорию{' '}
              <span className="font-semibold text-slate-900" data-id="element-10817">
                «{categoryToDelete}»
              </span>
              .
            </p>
            <p className="text-sm text-slate-500 mb-1" data-id="element-10818">
              {blogPosts.filter(p => p.category === categoryToDelete).length > 0 ? <>
                  В этой категории{' '}
                  <span className="font-semibold text-amber-600" data-id="element-10819">
                    {blogPosts.filter(p => p.category === categoryToDelete).length}
                  </span>{' '}
                  {(() => {
              const count = blogPosts.filter(p => p.category === categoryToDelete).length;
              if (count === 1) return 'статья';
              if (count >= 2 && count <= 4) return 'статьи';
              return 'статей';
            })()}
                  . Они не будут удалены, а станут{' '}
                  <span className="font-semibold" data-id="element-10820">«Без категории»</span>.
                </> : 'В этой категории нет статей.'}
            </p>
            <div className="flex gap-3 mt-6" data-id="element-10821">
              <button onClick={() => setCategoryToDelete(null)} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-10822">
                Отмена
              </button>
              <button onClick={() => performCategoryDeletion(categoryToDelete)} className="flex-1 px-4 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition-colors" data-id="element-10823">
                Удалить
              </button>
            </div>
          </div>
        </div>}
    </div>;
}